import { Composer } from './Composer'
import { HERO_PROMPTS } from '../starters'

export interface ChatHeroProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (query: string) => void
  onCancel: () => void
  isRunning: boolean
}

function timeGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 5) return 'Good night'
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/**
 * Landing state for an empty thread: greeting, gradient orb, an embedded
 * composer, and a small set of starter prompts. Once the first exchange
 * lands, `ChatView` swaps this out for `Thread` + the docked composer — this
 * component only ever renders while `exchanges.length === 0`.
 */
export function ChatHero({
  value,
  onChange,
  onSubmit,
  onCancel,
  isRunning,
}: ChatHeroProps) {
  return (
    <div className="scrollbar-thin flex h-full min-h-0 flex-1 flex-col items-center overflow-y-auto px-4">
      <div className="flex w-full max-w-2xl flex-1 flex-col items-center justify-center py-10">
        <div className="relative mb-7 h-16 w-16 shrink-0">
          <div className="aura-orb-glow absolute inset-0 rounded-full" />
          <div className="aura-orb absolute inset-0 rounded-full" />
        </div>

        <h1 className="text-center text-[26px] leading-tight font-semibold text-fg sm:text-[30px]">
          {timeGreeting()}.
        </h1>
        <p className="mt-1.5 text-center text-[15px] text-muted sm:text-base">
          What would you like to know about your data?
        </p>

        <Composer
          variant="hero"
          className="mt-8 w-full"
          value={value}
          onChange={onChange}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isRunning={isRunning}
        />

        <div className="mt-6 grid w-full grid-cols-1 gap-2.5 sm:grid-cols-3">
          {HERO_PROMPTS.map(({ title, description, query, icon: Icon }) => (
            <button
              key={title}
              type="button"
              onClick={() => onChange(query)}
              className="group flex flex-col items-start gap-2 rounded-xl border border-line bg-surface px-4 py-3.5 text-left transition-colors hover:border-line-strong hover:bg-hover"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sunken text-muted transition-colors group-hover:bg-accent-soft group-hover:text-accent">
                <Icon size={17} strokeWidth={1.75} />
              </span>
              <span className="text-[13px] font-medium text-fg">{title}</span>
              <span className="text-xs leading-snug text-subtle">
                {description}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
